import { Component, createRef } from 'react'
import './WebcamPhotoCapture.css'
import { SelectInput, SelectOption } from '../SelectInput/SelectInput'
import { Icon } from '../Icon/Icon'
import { devLog } from '@/methods/devLog'

interface WebcamPhotoCaptureProps {
  showFrame?: boolean
  onPhotoCapture?: (photoDataUrl: string | Blob) => void
  outputFormat?: 'image/png' | 'image/jpeg' | 'image/webp'
  outputType?: 'blob' | 'base64'
  debug?: boolean
}

interface WebcamPhotoCaptureState {
  videoDevices: MediaDeviceInfo[]
  selectedDevice?: { value: string; label: string }
  error?: string
  photoDataUrl?: string
  width?: number
  height?: number
  isLoading?: boolean
}

export class WebcamPhotoCapture extends Component<
  WebcamPhotoCaptureProps,
  WebcamPhotoCaptureState
> {
  videoRef = createRef<HTMLVideoElement>()
  hiddenCanvasRef = createRef<HTMLCanvasElement>()

  constructor(props: WebcamPhotoCaptureProps) {
    super(props)
    this.state = {
      videoDevices: [],
    }
  }

  error = (error: string) => {
    this.setState({ error })
  }

  setStreamWithDimensions = async (deviceId: string) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        deviceId: deviceId,
        width: { ideal: 20000 },
      },
    })

    let { width, height } = stream.getTracks()[0].getSettings()

    this.setState({ width, height })

    if (this.videoRef.current) {
      this.videoRef.current.srcObject = stream
    }

    this.setState({ isLoading: false })
  }

  preStreamInit = async (device: { label: string; value: string }) => {
    this.setState({ isLoading: true })

    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        deviceId: device.value,
      },
    })
    if (this.videoRef.current) {
      this.videoRef.current.srcObject = stream
    }

    this.setState({ selectedDevice: device })
  }

  initCamera = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const filteredDevices = devices.filter((device) => device.kind === 'videoinput')
    this.setState({ videoDevices: filteredDevices })

    if (filteredDevices.length > 0) {
      const selectedDevice = {
        value: filteredDevices[0].deviceId,
        label: filteredDevices[0].label,
      }

      //failsafe stream setup for mobile, before real setup in componentDidUpdate after waitForMoreCameraDevices
      await this.preStreamInit(selectedDevice)

      await this.waitForMoreCameraDevices()
    } else {
      this.error('No webcam found on this device.')
    }
  }

  waitForMoreCameraDevices = async () => {
    await new Promise((resolve) => setTimeout(resolve, 400))
    const devices = await navigator.mediaDevices.enumerateDevices()
    const filteredDevices = devices.filter((device) => device.kind === 'videoinput')
    this.setState({ videoDevices: filteredDevices })
  }

  async componentDidMount() {
    if (this.props.debug) {
      // log
    }

    try {
      this.initCamera()
    } catch (error) {
      devLog('Error accessing webcam:', error)
      try {
        // Retry after 400ms
        await new Promise((resolve) => setTimeout(resolve, 400))

        this.initCamera()
      } catch (error) {
        this.error('Error accessing webcam: ' + error)
      }
    }
  }

  async componentDidUpdate(_: WebcamPhotoCaptureProps, prevState: WebcamPhotoCaptureState) {
    if (
      prevState.videoDevices.length &&
      !prevState.videoDevices[0].deviceId &&
      this.state.videoDevices.length &&
      this.state.videoDevices[0].deviceId.length
    ) {
      await this.setStreamWithDimensions(this.state.videoDevices[0].deviceId)
    }

    if (
      this.state.selectedDevice !== prevState.selectedDevice &&
      this.state.selectedDevice?.value
    ) {
      try {
        await this.setStreamWithDimensions(this.state.selectedDevice.value)
      } catch (error) {
        devLog('Error while switching webcam:', error)
        this.setState({ error: 'Error while switching webcam.' })
      }
    }
  }

  downloadBlob = (blob: Blob, fileFormat: string) => {
    const blobUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = 'image.' + fileFormat.split('/')[1]
    document.body.appendChild(link)
    link.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      })
    )
    document.body.removeChild(link)
  }

  takePhoto = async () => {
    if (this.videoRef.current && this.hiddenCanvasRef.current) {
      const video = this.videoRef.current
      const canvas = this.hiddenCanvasRef.current
      const context = canvas.getContext('2d')

      canvas.width = this.state.width ?? 0
      canvas.height = this.state.height ?? 0

      if (this.props.debug) {
        devLog('canvas', canvas.width, canvas.height)
      }

      context?.drawImage(video, 0, 0, canvas.width, canvas.height)

      const fileFormat = this.props.outputFormat || 'image/png'

      // base64 for <img /> preview & base64 mode export
      const dataUrl = canvas.toDataURL(fileFormat)
      this.setState({ photoDataUrl: dataUrl })

      if (this.props.outputType === 'blob') {
        canvas.toBlob((blob) => {
          if (blob && this.props.onPhotoCapture) {
            this.props.onPhotoCapture(blob)

            if (this.props.debug) {
              this.downloadBlob(blob, fileFormat)
            }
          } else {
            this.setState({ error: 'Failed to create blob from taken photo' })
          }
        }, fileFormat)
      } else {
        if (this.props.onPhotoCapture) {
          this.props.onPhotoCapture(dataUrl)
        }
      }
    }
  }

  reset = () => {
    this.setState({ photoDataUrl: undefined })
  }

  render() {
    const { showFrame } = this.props

    const { error, selectedDevice, photoDataUrl, videoDevices, isLoading } = this.state

    const videoDevicesSelectOptions = videoDevices.map((device) => ({
      value: device.deviceId,
      label: device.label,
    }))
    const videoDevicesSelectedIndex = videoDevicesSelectOptions.findIndex(
      (item) => item.value === selectedDevice?.value
    )

    return (
      <div className="webcam__container onboarding-webcam-photo-capture-container">
        <div className="webcam__webcam-capture onboarding-webcam-photo-capture">
          <div className="webcam__video-container onboarding-webcam-photo-capture-video-container">
            {showFrame && !isLoading ? <div className="frame" /> : <></>}
            {error && <p className="error-message">{error}</p>}
            {!error && (
              <video
                autoPlay
                loop
                muted
                webkit-playsinline="true"
                playsInline
                className="video"
                ref={this.videoRef}
              />
            )}
            {photoDataUrl && <img src={photoDataUrl} alt="Captured" />}
            {!error && videoDevices.length > 1 && !photoDataUrl && (
              <div className="webcam__device-select onboarding-webcam-photo-capture-select">
                <Icon type="dots-horizontal" size="32px" />
                <SelectInput
                  id="deviceSelect"
                  options={videoDevicesSelectOptions}
                  value={videoDevicesSelectOptions[videoDevicesSelectedIndex]}
                  onChange={(value) => {
                    if (value && !Array.isArray(value)) {
                      this.preStreamInit(value as SelectOption)
                    }
                  }}
                  isSearchable={true}
                />
              </div>
            )}
          </div>

          <div className="webcam__hidden-source-wrap">
            <div className="webcam__hidden-source-scale">
              <canvas ref={this.hiddenCanvasRef} />
            </div>
          </div>

          {isLoading && <div className="webcam__loading-wrapper"></div>}
        </div>
      </div>
    )
  }
}
